// src/utils/retentionOpportunity.js
//
// Pass 80 — the ONE pure, DERIVED retention / next-best-action contract.
//
// Retention is ASSISTIVE, EXPLAINABLE, HUMAN-TRIGGERED. This model surfaces "who may need attention, why, and what an
// operator could do" — it NEVER sends, books, or contacts anyone. It is:
//   · clientId-NATIVE — commercial identity is `organizationId + clientId`; a no-Auth customer (clientUserId '') works.
//     `clientId` is never a `clientUserId`/`studentId`; no Auth identity is fabricated.
//   · DELIVERY-AWARE — "delivered service" is `isDeliveryBearing(lesson)` (never `booking.status === 'completed'`);
//     event time is the shared `lessonEventTime` precedence (never `updatedAt`).
//   · EXPLAINABLE — every action carries reasons/evidence derived from source facts.
//   · PARTICIPANT-SAFE — a family (one clientId, many participants) is ONE commercial retention row; participant
//     coaching context stays participant-scoped (no combined "family progression").
//   · NON-AI — AI never decides eligibility, action, service, timing, or identity (metamorphic).
//   · WRITE-FREE — pure; no Firestore. Operator actions are persisted elsewhere (retentionActions, bookingRequest).
//
// No `retentionOpportunities`/`nextBestActions`/`clientRiskScores` datastore is created — the queue is a read model.

import { isDeliveryBearing } from './lessonHandoff';
import { lessonEventTime, toEventTime } from './clientCoachingMemory';

const DAY_MS = 24 * 60 * 60 * 1000;
const s = (v) => String(v || '').trim();

const LIVE_BOOKING_STATUSES = new Set(['confirmed', 'upcoming', 'in_progress']);
const OPEN_REQUEST_STATUSES = new Set(['pending', 'new', 'submitted', 'open', 'in_review', 'awaiting']);

// ── derive current review/snooze state from the ONE operator-action history (retentionActions), not a profile cache ──
export function deriveRetentionActionState(retentionActions = [], now = Date.now()) {
  const sorted = (retentionActions || [])
    .map((a) => ({ ...a, at: toEventTime(a.createdAt) }))
    .sort((x, y) => y.at - x.at);
  const latestSnooze = sorted.find((a) => s(a.actionType) === 'snoozed' && toEventTime(a.snoozedUntil || a.until) > now);
  const latestReview = sorted.find((a) => s(a.actionType) === 'reviewed');
  const openRebookAction = sorted.find((a) => s(a.actionType) === 'rebook_request_created');
  return {
    snoozedUntil: latestSnooze ? toEventTime(latestSnooze.snoozedUntil || latestSnooze.until) : 0,
    isSnoozed: Boolean(latestSnooze),
    lastReviewedAt: latestReview ? latestReview.at : 0,
    lastRebookActionAt: openRebookAction ? openRebookAction.at : 0,
  };
}

// distinct DELIVERED service events (delivery-bearing lessons), newest first, by canonical event time.
function deliveredServiceEvents(lessons = []) {
  return (lessons || [])
    .filter((l) => isDeliveryBearing(l))
    .map((l) => ({ at: lessonEventTime(l), bookingId: s(l.bookingId), serviceId: s(l.serviceId || l.serviceIdSnapshot), lessonId: s(l.id || l.lessonId), sport: s(l.sport) }))
    .filter((e) => e.at > 0)
    .sort((a, b) => b.at - a.at);
}

// cadence from DISTINCT delivered service events only; unknown when <2 (never fabricated).
function computeCadence(events) {
  if (events.length < 2) return { days: null, known: false };
  const gaps = [];
  for (let i = 0; i < Math.min(events.length - 1, 8); i += 1) gaps.push((events[i].at - events[i + 1].at) / DAY_MS);
  const avg = gaps.reduce((sum, g) => sum + g, 0) / Math.max(gaps.length, 1);
  return { days: Math.round(avg), known: avg > 0 };
}

function liveBookings(bookings = []) {
  return (bookings || []).filter((b) => LIVE_BOOKING_STATUSES.has(s(b.status)));
}

// an upcoming CONFIRMED booking (future start). Cancelled/superseded excluded by the live-status filter.
function firstUpcomingBooking(bookings = [], now) {
  return liveBookings(bookings)
    .map((b) => ({ booking: b, at: toEventTime(b.startAt || b.scheduledAt) }))
    .filter((x) => x.at >= now)
    .sort((a, b) => a.at - b.at)
    .map((x) => x.booking)[0] || null;
}

// an OPEN rebook request (enquiry not yet booked). Distinct from a confirmed booking.
function openRebookRequest(bookingRequests = []) {
  return (bookingRequests || [])
    .filter((r) => s(r.sourceType) === 'client_profile_rebook' && OPEN_REQUEST_STATUSES.has(s(r.status)))
    .sort((a, b) => toEventTime(b.createdAt) - toEventTime(a.createdAt))[0] || null;
}

// §13/§14 — a service recommendation must be SUPPORTED: the exact last delivered/booked service, else an explicit
// discipline that maps to EXACTLY ONE active service. NEVER an arbitrary first-active catalog item.
function suggestService({ deliveredEvents, bookings, services }) {
  const activeServices = (services || []).filter((svc) => svc.active !== false);
  const byId = (id) => activeServices.find((svc) => s(svc.serviceId || svc.id) === s(id)) || null;
  // 1. exact last delivered service
  const lastDeliveredServiceId = deliveredEvents.map((e) => e.serviceId).find(Boolean);
  if (lastDeliveredServiceId && byId(lastDeliveredServiceId)) return { service: byId(lastDeliveredServiceId), basis: 'last_delivered_service' };
  // 2. exact last booked service
  const lastBooked = (bookings || []).slice().sort((a, b) => toEventTime(b.startAt || b.createdAt) - toEventTime(a.startAt || a.createdAt))[0];
  if (lastBooked && s(lastBooked.serviceId) && byId(lastBooked.serviceId)) return { service: byId(lastBooked.serviceId), basis: 'last_booked_service' };
  // 3. an explicit discipline that maps to EXACTLY ONE active service
  const discipline = s(lastBooked?.discipline);
  if (discipline) {
    const matches = activeServices.filter((svc) => s(svc.discipline) === discipline);
    if (matches.length === 1) return { service: matches[0], basis: 'unique_discipline_match' };
  }
  // no defensible mapping → null (operator selects). No arbitrary fallback.
  return { service: null, basis: 'none' };
}

// A canonical record's commercial group: its explicit `clientId` (canonical) wins; else a legacy Auth `user:<uid>`
// compatibility group. Two no-Auth clients never collapse — each distinct clientId is its own row.
function groupKeyOf({ clientId = '', requesterUserId = '', userId = '' }) {
  const cid = s(clientId);
  if (cid) return `client:${cid}`;
  const uid = s(requesterUserId) || s(userId);
  return uid ? `user:${uid}` : '';
}

// §20/§52 — the ONE commercial retention row per client, DERIVED from bulk canonical org reads. clientId-native (a
// no-Auth customer with only bookings/lessons appears); ONE row per clientId (deduped); participant coaching context
// stays participant-scoped. No persisted clientProfile required. Pure.
export function buildOrganizationRetentionOpportunities({
  organizationId = '',
  bookings = [],
  bookingRequests = [],
  lessons = [],
  membershipsByUserId = {},
  services = [],
  retentionActions = [],
  clientProfiles = [],
  coachingMemoryByClientId = {},
  now = Date.now(),
  includeSnoozed = false,
} = {}) {
  const groups = new Map(); // key -> { clientId, clientUserId, displayName, bookings, bookingRequests, lessons, retentionActions }
  const ensure = (key, seed) => {
    if (!groups.has(key)) groups.set(key, { clientId: '', clientUserId: '', displayName: '', bookings: [], bookingRequests: [], lessons: [], retentionActions: [], ...seed });
    return groups.get(key);
  };
  const absorb = (rec, bucket) => {
    const key = groupKeyOf(rec);
    if (!key) return;
    const g = ensure(key);
    if (!g.clientId && s(rec.clientId)) g.clientId = s(rec.clientId);
    if (!g.clientUserId && (s(rec.requesterUserId) || s(rec.userId) || s(rec.clientUserId))) g.clientUserId = s(rec.requesterUserId) || s(rec.userId) || s(rec.clientUserId);
    if (!g.displayName && (s(rec.requesterName) || s(rec.clientName))) g.displayName = s(rec.requesterName) || s(rec.clientName);
    g[bucket].push(rec);
  };

  (bookings || []).forEach((b) => absorb(b, 'bookings'));
  (bookingRequests || []).forEach((r) => absorb(r, 'bookingRequests'));
  (lessons || []).forEach((l) => absorb(l, 'lessons'));
  (clientProfiles || []).forEach((p) => {
    // a persisted profile enriches display + can seed a clientId group, but is NOT required for a candidate to exist
    const key = groupKeyOf({ clientId: p.clientId, userId: p.userId });
    if (!key) return;
    const g = ensure(key);
    if (!g.clientId && s(p.clientId)) g.clientId = s(p.clientId);
    if (!g.clientUserId && s(p.userId)) g.clientUserId = s(p.userId);
    if (!g.displayName && s(p.displayName)) g.displayName = s(p.displayName);
  });
  // retention action history joined by clientId (canonical) or clientUserId (legacy)
  (retentionActions || []).forEach((a) => {
    const key = s(a.clientId) ? `client:${s(a.clientId)}` : (s(a.clientUserId) ? `user:${s(a.clientUserId)}` : '');
    if (key && groups.has(key)) groups.get(key).retentionActions.push(a);
  });

  const opportunities = [];
  groups.forEach((g) => {
    const clientUserId = g.clientUserId;
    const memberships = clientUserId ? (membershipsByUserId[clientUserId] || []) : [];
    const opp = buildClientRetentionOpportunity({
      organizationId, clientId: g.clientId, clientUserId,
      displayName: g.displayName,
      bookings: g.bookings, bookingRequests: g.bookingRequests, lessons: g.lessons,
      memberships, services, retentionActions: g.retentionActions,
      coachingMemory: g.clientId ? coachingMemoryByClientId[g.clientId] || null : null,
      hasAuthAccount: Boolean(clientUserId),
      now,
    });
    opp.rowKey = g.clientId ? `client:${g.clientId}` : (clientUserId ? `user:${clientUserId}` : '');
    opportunities.push(opp);
  });

  const visible = includeSnoozed ? opportunities : opportunities.filter((o) => o.eligibility !== 'snoozed');
  // needs_attention first, then worth_reviewing, then by recency of last commercial activity
  const rank = { needs_attention: 0, worth_reviewing: 1, no_action: 2 };
  return visible.sort((a, b) => (rank[a.attentionState] - rank[b.attentionState]) || (b.lastCommercialActivityAt - a.lastCommercialActivityAt) || a.rowKey.localeCompare(b.rowKey));
}

export function buildClientRetentionOpportunity({
  organizationId = '',
  clientId = '',
  clientUserId = '',
  displayName = '',
  bookings = [],
  bookingRequests = [],
  lessons = [],
  memberships = [],
  services = [],
  retentionActions = [],
  coachingMemory = null,
  hasAuthAccount = undefined,
  now = Date.now(),
} = {}) {
  const authLinked = hasAuthAccount === undefined ? Boolean(clientUserId) : Boolean(hasAuthAccount);
  const deliveredEvents = deliveredServiceEvents(lessons);
  const lastDelivered = deliveredEvents[0] || null;
  const cadence = computeCadence(deliveredEvents);
  const upcomingBooking = firstUpcomingBooking(bookings, now);
  const rebookRequest = openRebookRequest(bookingRequests);
  const actionState = deriveRetentionActionState(retentionActions, now);

  const lastDeliveredServiceAt = lastDelivered ? lastDelivered.at : 0;
  const daysSinceLastDeliveredService = lastDeliveredServiceAt ? Math.floor((now - lastDeliveredServiceAt) / DAY_MS) : null;
  const lastCommercialActivityAt = Math.max(
    lastDeliveredServiceAt,
    ...(bookings || []).map((b) => toEventTime(b.startAt || b.createdAt)),
    ...(bookingRequests || []).map((r) => toEventTime(r.createdAt)),
    0,
  );

  // §15/§41 — package state is Auth-account-keyed. For a no-Auth client it is UNAVAILABLE, never a real 0.
  const activeMemberships = (memberships || []).filter((m) => s(m.status) === 'active');
  const remainingSessions = activeMemberships.reduce((sum, m) => sum + (Number(m.remainingSessions) || 0), 0);
  const remainingCredits = activeMemberships.reduce((sum, m) => sum + (Number(m.remainingCredits) || 0), 0);
  const packageState = authLinked
    ? { available: true, activeCount: activeMemberships.length, remainingSessions, remainingCredits, hasBalance: remainingSessions > 0 || remainingCredits > 0 }
    : { available: false, reason: 'no_linked_app_account' };

  const contactCapability = authLinked
    ? { canDirectMessage: true, reason: 'linked_app_account' }
    : { canDirectMessage: false, reason: 'no_linked_app_account' };

  const participantCount = coachingMemory?.participants?.length || 0;
  const isMultiParticipant = participantCount > 1;

  // ── deterministic next-best-action priority (§12) ──
  const reasons = [];
  const evidence = [];
  if (lastDelivered) evidence.push({ kind: 'last_delivered_service', at: lastDeliveredServiceAt, bookingId: lastDelivered.bookingId });
  if (cadence.known) evidence.push({ kind: 'cadence', days: cadence.days });

  const suggested = suggestService({ deliveredEvents, bookings, services });

  let eligibility = 'eligible';
  let nextBestAction = 'NO_ACTION';
  let suggestedTiming = 'insufficient_history';

  if (actionState.isSnoozed) {
    eligibility = 'snoozed';
    nextBestAction = 'NO_ACTION';
    reasons.push('Snoozed by an operator until a later date.');
  } else if (upcomingBooking) {
    eligibility = 'suppressed_upcoming';
    nextBestAction = 'NO_ACTION';
    reasons.push('An upcoming confirmed booking already exists — no rebook needed.');
  } else if (rebookRequest) {
    eligibility = 'open_request';
    nextBestAction = 'REVIEW_EXISTING_REQUEST';
    reasons.push('An open rebook request already exists — review it instead of creating a duplicate.');
  } else if (packageState.available && packageState.hasBalance) {
    nextBestAction = 'USE_EXISTING_CREDIT';
    reasons.push('Prepaid balance is available and there is no upcoming booking.');
  } else if (lastDelivered) {
    // delivered history exists — rebook only when the elapsed period warrants it (cadence, else a conservative window)
    const threshold = cadence.known ? cadence.days : 21;
    if (daysSinceLastDeliveredService !== null && daysSinceLastDeliveredService >= threshold) {
      nextBestAction = 'REBOOK';
      suggestedTiming = 'past_cadence';
      reasons.push(cadence.known
        ? `Last delivered service was ${daysSinceLastDeliveredService} days ago, past the typical ~${cadence.days}-day cadence.`
        : `Last delivered service was ${daysSinceLastDeliveredService} days ago with no upcoming booking.`);
    } else if (daysSinceLastDeliveredService !== null && cadence.known && daysSinceLastDeliveredService >= cadence.days - 7) {
      nextBestAction = 'FOLLOW_UP';
      suggestedTiming = 'approaching_cadence';
      reasons.push('Approaching the typical rebook window based on delivered-service cadence.');
    } else {
      suggestedTiming = 'recently_delivered';
      reasons.push('Recently delivered — no action needed yet.');
    }
  } else {
    // no delivered evidence — do NOT claim a service happened (a completed booking is not a delivered lesson)
    suggestedTiming = 'delivery_not_confirmed';
    reasons.push('No delivered-service evidence yet — no rebook recommendation.');
  }

  // a REBOOK/USE_CREDIT action with no defensible service still surfaces, but flags operator service selection.
  if ((nextBestAction === 'REBOOK') && !suggested.service) {
    reasons.push('No prior service could be determined — operator should choose a service.');
  }

  const attentionState = (nextBestAction === 'REBOOK' || nextBestAction === 'USE_EXISTING_CREDIT')
    ? 'needs_attention'
    : (nextBestAction === 'FOLLOW_UP' || nextBestAction === 'REVIEW_EXISTING_REQUEST')
      ? 'worth_reviewing'
      : 'no_action';

  return {
    organizationId: s(organizationId),
    clientId: s(clientId),
    clientUserId: s(clientUserId),
    displayName: s(displayName),
    hasAuthAccount: authLinked,
    eligibility,
    attentionState,
    lastDeliveredService: lastDelivered ? { at: lastDeliveredServiceAt, bookingId: lastDelivered.bookingId, serviceId: lastDelivered.serviceId } : null,
    lastDeliveredServiceAt,
    daysSinceLastDeliveredService,
    lastCommercialActivityAt,
    cadence,
    upcomingBooking: upcomingBooking ? { bookingId: s(upcomingBooking.bookingId || upcomingBooking.id), at: toEventTime(upcomingBooking.startAt || upcomingBooking.scheduledAt), status: s(upcomingBooking.status) } : null,
    openRebookRequest: rebookRequest ? { bookingRequestId: s(rebookRequest.bookingRequestId || rebookRequest.id), at: toEventTime(rebookRequest.createdAt), status: s(rebookRequest.status) } : null,
    nextBestAction,
    suggestedService: suggested.service ? { serviceId: s(suggested.service.serviceId || suggested.service.id), name: s(suggested.service.name), basis: suggested.basis } : null,
    suggestedTiming,
    packageState,
    contactCapability,
    participantCount,
    isMultiParticipant,
    snoozedUntil: actionState.snoozedUntil,
    lastReviewedAt: actionState.lastReviewedAt,
    evidence,
    reasons,
  };
}
